import type { Edge } from '@xyflow/react';
import { toast } from 'sonner';
import type { PipelineNode, SourceNode } from './types/canvas.types';
import { getAdpSessionNode, type AdpNodeRecord } from '../../hooks/useAdpSessionStore';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NodeType = PipelineNode['type'];

export interface IncomingNodeCheckResult {
    /** Whether the target node has at least one incoming node of the requested type. */
    hasIncoming: boolean;
    /** The first matching incoming node, if found. */
    incomingNode: PipelineNode | undefined;
}

// ─── Core util ────────────────────────────────────────────────────────────────

/**
 * Checks whether a given node (`targetNodeId`) has at least one incoming
 * connected node of the specified `requiredType`.
 *
 * @param targetNodeId  - The id of the node you want to inspect.
 * @param requiredType  - The node type that must be present as an incoming neighbour.
 * @param nodes         - The full list of nodes currently on the canvas.
 * @param edges         - The full list of edges currently on the canvas.
 * @returns             An object with `hasIncoming` flag and the first matching `incomingNode`.
 *
 * @example
 * const { hasIncoming } = getIncomingNodeOfType(id, 'source', nodes, edges);
 */
export function getIncomingNodeOfType(
    targetNodeId: string,
    requiredType: NodeType,
    nodes: PipelineNode[],
    edges: Edge[]
): IncomingNodeCheckResult {
    // Collect all source-node ids that have an edge pointing at targetNodeId
    const incomingNodeIds = edges
        .filter((edge) => edge.target === targetNodeId)
        .map((edge) => edge.source);

    const incomingNode = nodes.find(
        (node) => incomingNodeIds.includes(node.id) && node.type === requiredType
    ) as PipelineNode | undefined;

    return {
        hasIncoming: incomingNode !== undefined,
        incomingNode,
    };
}

// ─── Guard helpers ────────────────────────────────────────────────────────────

/**
 * Returns `true` when the node has an incoming source node.
 * Shows a toast error and returns `false` otherwise.
 *
 * Intended as a one-liner guard at the top of settings-open handlers.
 *
 * @example
 * if (!requiresIncomingSource(id, nodes, edges, 'Filter')) return;
 */
export function requiresIncomingSource(
    targetNodeId: string,
    nodes: PipelineNode[],
    edges: Edge[],
    /** Human-readable label shown in the error toast, e.g. "Filter" or "Destination". */
    nodeLabel: string
): boolean {
    const { hasIncoming } = getIncomingNodeOfType(targetNodeId, 'source', nodes, edges);

    if (!hasIncoming) {
        toast.error(
            `"${nodeLabel}" requires a connected Source node. Please connect a Source before opening settings.`
        );
    }

    return hasIncoming;
}

/**
 * True when some path of incoming edges from `targetNodeId` reaches a `source` node
 * (walking through `transformation` / `integration` hops). Use for destinations and
 * transforms where the graph is Source → … → target, not only Source → target.
 */
export function hasUpstreamSourceNode(
    targetNodeId: string,
    nodes: PipelineNode[],
    edges: Edge[]
): boolean {
    const visited = new Set<string>();
    const stack: string[] = [targetNodeId];

    while (stack.length > 0) {
        const nodeId = stack.pop()!;
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);

        const upstreamIds = edges
            .filter((e) => e.target === nodeId)
            .map((e) => e.source);

        for (const upstreamId of upstreamIds) {
            const node = nodes.find((n) => n.id === upstreamId);
            if (!node) continue;
            if (node.type === 'source') return true;
            if (node.type === 'transformation' || node.type === 'integration') {
                stack.push(upstreamId);
            }
        }
    }

    return false;
}

/** Like {@link requiresIncomingSource} but allows Source upstream through Transformation / Integration. */
export function requiresUpstreamSource(
    targetNodeId: string,
    nodes: PipelineNode[],
    edges: Edge[],
    nodeLabel: string
): boolean {
    const ok = hasUpstreamSourceNode(targetNodeId, nodes, edges);
    if (!ok) {
        toast.error(
            `"${nodeLabel}" needs a pipeline path from a Source node. Connect upstream nodes, then try again.`
        );
    }
    return ok;
}

/**
 * Generic version of the guard above — checks for any required incoming node type.
 *
 * @example
 * if (!requiresIncomingNodeType(id, 'transformation', nodes, edges, 'Destination')) return;
 */
export function requiresIncomingNodeType(
    targetNodeId: string,
    requiredType: NodeType,
    nodes: PipelineNode[],
    edges: Edge[],
    nodeLabel: string
): boolean {
    const { hasIncoming } = getIncomingNodeOfType(targetNodeId, requiredType, nodes, edges);

    if (!hasIncoming) {
        toast.error(
            `"${nodeLabel}" requires a connected ${requiredType.charAt(0).toUpperCase() + requiredType.slice(1)} node. Please connect one before opening settings.`
        );
    }

    return hasIncoming;
}

// ─── Upstream source collection ───────────────────────────────────────────────

/**
 * A resolved upstream source node together with its session-store data.
 */
export interface UpstreamSourceEntry {
    /** The ReactFlow node id. */
    nodeId: string;
    /** The full ReactFlow node object (type, data, position, …). */
    node: SourceNode;
    /**
     * The session-store record for this node (configuration data written by
     * `useAdpSessionStore` / `useSourceData`).  `null` when nothing has been
     * stored yet.
     */
    sessionData: AdpNodeRecord | null;
}

/**
 * Walks the canvas graph **upstream** from `startNodeId` (following incoming
 * edges at every level) and collects every `source` node it encounters,
 * regardless of how many intermediate `transformation` / `integration` hops
 * lie between them.
 *
 * The traversal is a standard iterative BFS/DFS with a visited-set so cycles
 * can never cause an infinite loop.
 *
 * Each result entry contains:
 * - `nodeId`      – the ReactFlow node id
 * - `node`        – the full `SourceNode` object (data, position, type, …)
 * - `sessionData` – the matching record from `useAdpSessionStore` (or `null`)
 *
 * @param startNodeId - The node you want to inspect (e.g. a transformation or
 *                      destination node).
 * @param nodes       - All nodes currently on the canvas.
 * @param edges       - All edges currently on the canvas.
 * @returns           An array of {@link UpstreamSourceEntry} objects, one per
 *                    unique upstream source node found.
 *
 * @example
 * // Inside a component that has access to Redux canvas state:
 * const nodes = useAppSelector(selectCanvasNodes);
 * const edges = useAppSelector(selectCanvasEdges);
 *
 * const sources = getAllUpstreamSourceNodes(myNodeId, nodes, edges);
 * // sources[0].node.data.sourceType  → e.g. "postgres"
 * // sources[0].sessionData           → { columns: [...], ... }
 */
export function getAllUpstreamSourceNodes(
    startNodeId: string,
    nodes: PipelineNode[],
    edges: Edge[]
): UpstreamSourceEntry[] {
    // Build a quick id → node lookup to avoid repeated .find() calls.
    const nodeMap = new Map<string, PipelineNode>(nodes.map((n) => [n.id, n]));

    const results: UpstreamSourceEntry[] = [];
    const visited = new Set<string>();
    const queue: string[] = [startNodeId];

    while (queue.length > 0) {
        const currentId = queue.shift()!;

        if (visited.has(currentId)) continue;
        visited.add(currentId);

        // Find every node that has an edge pointing INTO currentId.
        const parentIds = edges
            .filter((e) => e.target === currentId)
            .map((e) => e.source);

        for (const parentId of parentIds) {
            if (visited.has(parentId)) continue;

            const parentNode = nodeMap.get(parentId);
            if (!parentNode) continue;

            if (parentNode.type === 'source') {
                // Found a source – collect it with its session data.
                results.push({
                    nodeId: parentId,
                    node: parentNode as SourceNode,
                    sessionData: getAdpSessionNode(parentId),
                });
                // Mark visited so we don't add it twice, but don't traverse
                // further upstream from a source (sources have no inputs).
                visited.add(parentId);
            } else {
                // Intermediate node (transformation / integration / destination) –
                // keep walking upstream.
                queue.push(parentId);
            }
        }
    }

    return results;
}

/**
 * Traverse the edge list upstream from `startNodeId` and return all unique
 * ancestor node ids (at any depth). The `startNodeId` itself is excluded.
 */
export function getUpstreamNodeIds(
    startNodeId: string,
    edges: Edge[],
): string[] {
    const visited = new Set<string>();
    const queue: string[] = [startNodeId];

    while (queue.length > 0) {
        const current = queue.shift();
        if (!current) continue;

        for (const edge of edges) {
            if (edge.target === current && !visited.has(edge.source)) {
                visited.add(edge.source);
                queue.push(edge.source);
            }
        }
    }

    return Array.from(visited);
}
